import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../common/types';
import { useMusicVideoEditor } from '../MusicVideoEditorContext';
import { panelMessages } from '../messages/panelMessages';
import { SidePanel } from '../panels/SidePanel';
import { TOOL_ICONS } from '../panels/ToolRail';
import { PreviewFrame } from '../preview/PreviewFrame';
import { Transport } from '../preview/Transport';
import { editorMessages } from '../messages/editorMessages';
import { MiniStrip } from './MiniStrip';
import { SectionList } from './SectionList';
import { SectionSheet } from './SectionSheet';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the editor; each part uses its own slice of it */
import styles from '../MusicVideoEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

type PhoneTab = 'sections' | 'text' | 'lyrics' | 'look';
const PHONE_TABS: readonly PhoneTab[] = ['sections', 'text', 'lyrics', 'look'];
const TAB_MESSAGES = {
  sections: panelMessages.phoneSections,
  text: editorMessages.toolText,
  lyrics: editorMessages.toolLyrics,
  look: editorMessages.toolLook,
} as const;

interface PhoneEditorProps {
  /** The top bar: `PhoneTop` in account mode, `ShowcasePhoneTop` in showcase mode (design c5). */
  top: React.ReactNode;
  notices: React.ReactNode;
}

/**
 * The phone layout (design f1, below 720 px): the player, the thin strip of the whole cut, the
 * sections in place of the clip timeline, and the bottom tabs (Sections, Text, Lyrics, Look). A
 * section opens its sheet (f2): swap a clip, keep it on recut, recut the section.
 */
const PhoneEditorComponent: React.FC<PhoneEditorProps> = ({ top, notices }) => {
  const intl = useIntl();
  const { setTool } = useMusicVideoEditor();
  const [tab, setTab] = useState<PhoneTab>('sections');
  const [openSection, setOpenSection] = useState<number | null>(null);
  const pick = (next: PhoneTab) => {
    setTab(next);
    if (next !== 'sections') {
      setTool(next);
    }
  };
  return (
    <div className={styles.phone}>
      {top}
      <div className={styles.stageColumn}>
        {notices}
        <PreviewFrame fill />
      </div>
      <div className={styles.phoneTransport}>
        <Transport showSafe={false} wide liveLabel={intl.formatMessage(panelMessages.phoneNoCredits)} />
      </div>
      <MiniStrip />
      {tab === 'sections' ? (
        <div className={styles.phoneScroll}>
          <SectionList onOpen={setOpenSection} />
        </div>
      ) : (
        <SidePanel className={clsx(styles.pane, styles.phonePanel)} />
      )}
      <nav className={styles.phoneTabs} aria-label={intl.formatMessage(editorMessages.toolsLabel)}>
        {PHONE_TABS.map((item) => (
          <button
            key={item}
            type="button"
            className={clsx(styles.railTool, item === tab && styles.railToolOn)}
            aria-pressed={item === tab}
            onClick={() => pick(item)}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              {item === 'sections' ? TOOL_ICONS.footage : TOOL_ICONS[item]}
            </svg>
            {intl.formatMessage(TAB_MESSAGES[item])}
          </button>
        ))}
      </nav>
      {openSection !== null && <SectionSheet sectionIndex={openSection} onClose={() => setOpenSection(null)} />}
    </div>
  );
};

export const PhoneEditor = TypedMemo(PhoneEditorComponent);
